import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import { X, Save, Plus, Trash2, MapPin, AlertCircle, CheckCircle2 } from 'lucide-react';
import { createTileLayer } from '../utils/mapTiles';

export default function HubCardModal({
  hub,
  isOpen,
  onClose,
  onSaveHub,
  onDeleteHub
}) {
  if (!isOpen || !hub) return null;

  const isCreate = !hub.id;

  const [formData, setFormData] = useState({
    id: hub.id || null,
    name: hub.name || '',
    street: hub.street || hub.address || '',
    postal_code: hub.postal_code || '',
    city: hub.city || '',
    country: hub.country || 'Suisse',
    latitude: parseFloat(hub.latitude) || 46.5197,
    longitude: parseFloat(hub.longitude) || 6.6323,
    geocoding_precision: hub.geocoding_precision || 'exact',
    metadata: typeof hub.metadata === 'object' ? JSON.stringify(hub.metadata, null, 2) : (hub.metadata || '{}')
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const markerRef = useRef(null);

  // Sync state when hub changes
  useEffect(() => {
    if (hub) {
      setFormData({
        id: hub.id || null,
        name: hub.name || '',
        street: hub.street || hub.address || '',
        postal_code: hub.postal_code || '',
        city: hub.city || '',
        country: hub.country || 'Suisse',
        latitude: parseFloat(hub.latitude) || 46.5197,
        longitude: parseFloat(hub.longitude) || 6.6323,
        geocoding_precision: hub.geocoding_precision || 'exact',
        metadata: typeof hub.metadata === 'object' ? JSON.stringify(hub.metadata, null, 2) : (hub.metadata || '{}')
      });
      setShowDeleteConfirm(false);
      setErrorMsg('');
    }
  }, [hub]);

  // Clean up leaflet instance when modal closes
  useEffect(() => {
    if (!isOpen && leafletMap.current) {
      leafletMap.current.remove();
      leafletMap.current = null;
      markerRef.current = null;
    }
  }, [isOpen]);

  // Leaflet map setup and marker sync
  useEffect(() => {
    if (!isOpen || !mapRef.current) return;

    const currentLat = parseFloat(formData.latitude) || 46.5197;
    const currentLng = parseFloat(formData.longitude) || 6.6323;

    if (!leafletMap.current) {
      const map = L.map(mapRef.current, {
        center: [currentLat, currentLng],
        zoom: 14,
        zoomControl: true
      });

      createTileLayer('pale').addTo(map);

      // Click on map to place/move the hub marker
      map.on('click', (e) => {
        const newLat = parseFloat(e.latlng.lat.toFixed(6));
        const newLng = parseFloat(e.latlng.lng.toFixed(6));
        setFormData(prev => ({
          ...prev,
          latitude: newLat,
          longitude: newLng,
          geocoding_precision: 'manual'
        }));
      });

      leafletMap.current = map;
    }

    const map = leafletMap.current;

    if (markerRef.current) {
      map.removeLayer(markerRef.current);
    }

    let pinColor = '#22c55e';
    if (formData.geocoding_precision === 'approximate') pinColor = '#f59e0b';
    if (formData.geocoding_precision === 'wrong') pinColor = '#ef4444';

    const customIcon = L.divIcon({
      className: 'draggable-hub-marker',
      html: `<div style="background:${pinColor}; color:#fff; border:2px solid #fff; border-radius:50%; width:34px; height:34px; display:flex; align-items:center; justify-content:center; font-size:17px; box-shadow:0 4px 10px rgba(0,0,0,0.5); cursor:grab;">🏭</div>`,
      iconSize: [34, 34],
      iconAnchor: [17, 17]
    });

    const marker = L.marker([currentLat, currentLng], {
      icon: customIcon,
      draggable: true
    }).addTo(map);

    marker.on('dragend', (e) => {
      const latLng = e.target.getLatLng();
      const newLat = parseFloat(latLng.lat.toFixed(6));
      const newLng = parseFloat(latLng.lng.toFixed(6));

      setFormData(prev => ({
        ...prev,
        latitude: newLat,
        longitude: newLng,
        geocoding_precision: 'manual'
      }));
    });

    markerRef.current = marker;
    map.setView([currentLat, currentLng], map.getZoom());

    const t = setTimeout(() => {
      if (leafletMap.current) {
        leafletMap.current.invalidateSize();
      }
    }, 200);

    return () => clearTimeout(t);
  }, [formData.latitude, formData.longitude, formData.geocoding_precision, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg('');
    try {
      let metaJson = {};
      if (formData.metadata) {
        if (typeof formData.metadata === 'object') {
          metaJson = formData.metadata;
        } else {
          try {
            metaJson = JSON.parse(formData.metadata);
          } catch (err) {
            metaJson = { raw: formData.metadata };
          }
        }
      }
      await onSaveHub(formData.id, { ...formData, metadata: metaJson });
      onClose();
    } catch (err) {
      console.error('Error saving hub:', err);
      setErrorMsg(err.message || "Erreur lors de l'enregistrement du hub");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!formData.id) return;
    setIsDeleting(true);
    setErrorMsg('');
    try {
      if (onDeleteHub) {
        await onDeleteHub(formData.id);
      } else {
        const res = await fetch(`/api/hubs/${formData.id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error('Échec de suppression');
      }
      onClose();
    } catch (err) {
      console.error('Error deleting hub:', err);
      setErrorMsg(err.message || 'Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1200 }}>
      <div
        className="overlay-card"
        style={{
          width: '780px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
          overflow: 'hidden',
          borderRadius: '12px',
          background: '#0f172a',
          border: '1px solid #334155',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.75)'
        }}
      >
        {/* HEADER BAR */}
        <div
          style={{
            background: '#1e293b',
            padding: '14px 20px',
            borderBottom: '1px solid #334155',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span
              style={{
                background: isCreate ? '#22c55e' : '#38bdf8',
                color: '#0f172a',
                padding: '4px 8px',
                borderRadius: '6px',
                fontWeight: '900',
                fontSize: '0.8rem',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              🏭 {isCreate ? 'NOUVEAU' : `#${formData.id}`}
            </span>
            <div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#f8fafc', margin: 0 }}>
                {isCreate ? 'Créer un Hub / Dépôt' : `Éditer Hub #${formData.id} - ${formData.name}`}
              </h2>
              <div style={{ fontSize: '0.74rem', color: '#94a3b8', marginTop: '2px' }}>
                {isCreate
                  ? 'Définissez le nom, l’adresse et ajustez précisément le point GPS sur la carte'
                  : `📍 ${formData.street || 'Sans rue'}, ${formData.postal_code} ${formData.city}`}
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center'
            }}
            title="Fermer"
          >
            <X size={20} />
          </button>
        </div>

        {/* BODY FORM */}
        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '14px', color: '#f8fafc' }}>
          
          {errorMsg && (
            <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid #ef4444', borderRadius: '6px', padding: '8px 12px', fontSize: '0.78rem', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <AlertCircle size={15} /> {errorMsg}
            </div>
          )}

          {/* NOM DU HUB */}
          <div>
            <label style={{ fontSize: '0.74rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>
              Nom du Hub / Dépôt *
            </label>
            <input
              type="text"
              className="search-input"
              required
              placeholder="ex: Centre Logistique Lausanne-Renens"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              style={{ width: '100%', padding: '7px 10px', fontSize: '0.85rem', fontWeight: '600' }}
            />
          </div>

          {/* ADRESSE COMPLETE */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '10px' }}>
            <div>
              <label style={{ fontSize: '0.74rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>
                Rue et Numéro *
              </label>
              <input
                type="text"
                className="search-input"
                required
                placeholder="ex: Route de Prilly 25"
                value={formData.street}
                onChange={e => setFormData({ ...formData, street: e.target.value })}
                style={{ width: '100%', padding: '6px 10px', fontSize: '0.82rem' }}
              />
            </div>

            <div>
              <label style={{ fontSize: '0.74rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>
                Code Postal *
              </label>
              <input
                type="text"
                className="search-input"
                required
                placeholder="ex: 1004"
                value={formData.postal_code}
                onChange={e => setFormData({ ...formData, postal_code: e.target.value })}
                style={{ width: '100%', padding: '6px 10px', fontSize: '0.82rem' }}
              />
            </div>

            <div>
              <label style={{ fontSize: '0.74rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>
                Ville *
              </label>
              <input
                type="text"
                className="search-input"
                required
                placeholder="ex: Lausanne"
                value={formData.city}
                onChange={e => setFormData({ ...formData, city: e.target.value })}
                style={{ width: '100%', padding: '6px 10px', fontSize: '0.82rem' }}
              />
            </div>

            <div>
              <label style={{ fontSize: '0.74rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>
                Pays
              </label>
              <input
                type="text"
                className="search-input"
                placeholder="Suisse"
                value={formData.country}
                onChange={e => setFormData({ ...formData, country: e.target.value })}
                style={{ width: '100%', padding: '6px 10px', fontSize: '0.82rem' }}
              />
            </div>
          </div>

          {/* COORDONNÉES GPS & PRÉCISION (LIKE CLIENTCARDMODAL) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr', gap: '10px' }}>
            <div>
              <label style={{ fontSize: '0.74rem', color: '#38bdf8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>
                Latitude (GPS) *
              </label>
              <input
                type="number"
                step="0.000001"
                className="search-input"
                required
                value={formData.latitude}
                onChange={e => setFormData({ ...formData, latitude: parseFloat(e.target.value) || 0, geocoding_precision: 'manual' })}
                style={{ width: '100%', padding: '6px 10px', fontSize: '0.82rem' }}
              />
            </div>

            <div>
              <label style={{ fontSize: '0.74rem', color: '#38bdf8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>
                Longitude (GPS) *
              </label>
              <input
                type="number"
                step="0.000001"
                className="search-input"
                required
                value={formData.longitude}
                onChange={e => setFormData({ ...formData, longitude: parseFloat(e.target.value) || 0, geocoding_precision: 'manual' })}
                style={{ width: '100%', padding: '6px 10px', fontSize: '0.82rem' }}
              />
            </div>

            <div>
              <label style={{ fontSize: '0.74rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>
                Précision Géocodage
              </label>
              <select
                className="search-input"
                value={formData.geocoding_precision}
                onChange={e => setFormData({ ...formData, geocoding_precision: e.target.value })}
                style={{ width: '100%', padding: '6px 10px', fontSize: '0.82rem', fontWeight: '700' }}
              >
                <option value="exact">Exact / Certain (Vert)</option>
                <option value="manual">Manual / Carte (Vert)</option>
                <option value="approximate">Approximatif (Orange)</option>
                <option value="wrong">Problème (Rouge)</option>
              </select>
            </div>
          </div>

          {/* INTERACTIVE LEAFLET MAP */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ fontSize: '0.74rem', color: '#94a3b8', fontWeight: '700' }}>
                🗺️ Positionnement Carte (Glissez le marqueur ou cliquez pour ajuster les coordonnées GPS)
              </label>
              <span style={{ fontSize: '0.7rem', color: '#38bdf8' }}>
                {Number(formData.latitude).toFixed(6)}, {Number(formData.longitude).toFixed(6)}
              </span>
            </div>

            <div
              style={{
                height: '240px',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #334155',
                boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.4)'
              }}
            >
              <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
            </div>
          </div>

          {/* METADATA (JSON) */}
          <div>
            <label style={{ fontSize: '0.74rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '3px' }}>
              Métadonnées (JSON)
            </label>
            <textarea
              className="search-input"
              style={{ width: '100%', minHeight: '52px', fontFamily: 'monospace', fontSize: '0.75rem', padding: '6px 10px', resize: 'vertical' }}
              placeholder='{"key": "value"}'
              value={formData.metadata}
              onChange={e => setFormData({ ...formData, metadata: e.target.value })}
            />
          </div>

          {/* FOOTER ACTIONS */}
          <div
            style={{
              borderTop: '1px solid #334155',
              paddingTop: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: '4px'
            }}
          >
            <div>
              {!isCreate && (
                !showDeleteConfirm ? (
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    style={{
                      background: 'none',
                      border: '1px solid rgba(239, 68, 68, 0.4)',
                      color: '#ef4444',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <Trash2 size={13} /> Supprimer le Hub
                  </button>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '0.74rem', color: '#ef4444', fontWeight: '700' }}>
                      Confirmer la suppression ?
                    </span>
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      style={{
                        background: '#ef4444',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontSize: '0.72rem',
                        fontWeight: '800',
                        cursor: 'pointer'
                      }}
                    >
                      {isDeleting ? 'Suppression...' : 'Oui, supprimer'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      style={{
                        background: '#334155',
                        color: '#cbd5e1',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontSize: '0.72rem',
                        cursor: 'pointer'
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                )
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                type="button"
                className="btn-action"
                onClick={onClose}
                disabled={isSubmitting}
                style={{ padding: '7px 16px', fontSize: '0.8rem' }}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn-action"
                disabled={isSubmitting}
                style={{
                  background: isCreate ? '#22c55e' : '#38bdf8',
                  color: '#0f172a',
                  border: 'none',
                  fontWeight: '800',
                  padding: '7px 20px',
                  fontSize: '0.82rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {isSubmitting ? (
                  'Enregistrement...'
                ) : isCreate ? (
                  <>
                    <Plus size={15} /> Créer le Hub
                  </>
                ) : (
                  <>
                    <Save size={15} /> Enregistrer
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
